import type { CSVImportMapping } from '@ikegps/core-api';
import { IkeFieldType } from '@ikegps/core-api';
import type { FormModelField } from '../../form';
import type { FormModel } from '../../form/form';
import { IkeModel } from '../../index';
import type { CollectionModel } from '../collection.data';
import type { CollectionModelField } from '../collection.field';
import { CollectionModelFieldLocation } from '../fields/location.field';
import { CollectionModelFieldNestedList } from '../fields/nested.list.field';
import { CollectionModelFieldSelectList } from '../fields/select.list.field';
import { CollectionModelFieldText } from '../fields/text.field';
import type { IkeCSV } from './csv';

export type MapperFunction = (field: CollectionModelField, val: string, key?: string) => boolean;

const MappingFunctions: { [key: string]: MapperFunction } = {
	// [IkeFieldType.NUMBER]: (field: CollectionModelFieldNumber, val: string) => field.Mutator.manual(val),
	[IkeFieldType.TEXT]: (field: CollectionModelField, val: string) => {
		if (!CollectionModelFieldText.isTextField(field)) {
			return false;
		}
		if (val == null || val === '') {
			return true;
		}
		field.Mutator.manual(String(val));
		return true;
	},
	[IkeFieldType.SELECT_LIST]: (field: CollectionModelField, valIn: string) => {
		if (!CollectionModelFieldSelectList.isSelectListField(field)) {
			return false;
		}
		if (valIn == null || valIn === '') {
			return true;
		}

		const val = String(valIn);
		field.Mutator.manual(val);
		const hasValue = field.hasValue();
		if (hasValue) {
			return true;
		}

		// Attempt to match based on the schema options
		const textMatch = field.schema.options.find((opt) => opt.text.toLowerCase() === val.toLowerCase());
		if (textMatch) {
			field.Mutator.manual(textMatch.value);
		}
		return field.hasValue();
	},
	[IkeFieldType.NESTED_LIST]: (field: CollectionModelField, val: string) => {
		if (!CollectionModelFieldNestedList.isNestedListField(field)) {
			return false;
		}
		if (val == null || val === '') {
			return true;
		}
		field.Mutator.manual(CollectionModelFieldNestedList.parse(val));
		return field.hasValue();
	},
	[IkeFieldType.LOCATION]: (field: CollectionModelField, val: string, key?: string) => {
		if (!CollectionModelFieldLocation.isLocationField(field)) {
			return false;
		}
		const oldValue = Object.assign({}, field.value);
		const value = parseFloat(val);
		if (isNaN(value) || isNaN(val as any)) {
			return false;
		}

		switch (key) {
			case 'lat':
				oldValue.latitude = parseFloat(val);
				field.Mutator.manual(oldValue);
				return field.value.latitude === oldValue.latitude;
			case 'lng':
				oldValue.longitude = parseFloat(val);
				field.Mutator.manual(oldValue);
				return field.value.longitude === oldValue.longitude;
			default:
				throw new Error('Invalid location key: "' + key + "'");
		}
	},
};

/**
 * Lower cased alias's for the keys in the mapping process.
 */
export const DefaultKeyMappings = {
	lat: ['lat', 'latitude'],
	lng: ['lng', 'longitude', 'long'],
};

export interface IkeCSVMapping {
	field: FormModelField;
	fieldKey?: 'lat' | 'lng';

	csvIndex: number;
	isValid: boolean;
}

export class IkeCSVMapper {
	csv: IkeCSV = null;
	form: FormModel = null;

	/** List of fields that can be mapped to */
	mappings: IkeCSVMapping[] = [];
	/** List of the collections that have been created by these mappings */
	processed: CollectionModel[] = [];

	constructor(csv: IkeCSV, form: FormModel) {
		this.csv = csv;
		this.form = form;

		for (const field of this.form.fields) {
			if (field.type === 'location') {
				this.mappings.push({ field, fieldKey: 'lat', csvIndex: -1, isValid: true });
				this.mappings.push({ field, fieldKey: 'lng', csvIndex: -1, isValid: true });
			} else if (MappingFunctions[field.type]) {
				this.mappings.push({ field, csvIndex: -1, isValid: true });
			}
		}
	}

	toJSON(): CSVImportMapping[] {
		const output: CSVImportMapping[] = [];
		for (const mapping of this.mappings) {
			if (mapping.csvIndex === -1) {
				continue;
			}
			output.push({
				fieldId: mapping.field.id,
				fieldKey: mapping.fieldKey,
				csvHeader: this.csv.headers[mapping.csvIndex],
			});
		}
		return output;
	}

	getMapping(fieldId: string, fieldKey?: 'lat' | 'lng'): IkeCSVMapping {
		for (const mapping of this.mappings) {
			if (mapping.field.id !== fieldId) {
				continue;
			}
			if (fieldKey == null || fieldKey === mapping.fieldKey) {
				return mapping;
			}
		}
		return null;
	}

	setMapping(mapping: IkeCSVMapping, csvHeaderIndex: null | number, shouldProcess = true) {
		if (csvHeaderIndex == null || isNaN(csvHeaderIndex) || csvHeaderIndex < 0) {
			mapping.csvIndex = -1;
		} else {
			mapping.csvIndex = csvHeaderIndex;
		}

		if (shouldProcess) {
			this.processRows();
		}
	}

	getCollectionValue(mapping: IkeCSVMapping, index: number): { value: string; isValid: boolean; raw: string } {
		const collection = this.processed[index];
		const raw: string[] = this.csv.rows[index];
		const rawValue = raw[mapping.csvIndex];

		const field = collection.getField(mapping.field.id);
		if (CollectionModelFieldLocation.isLocationField(field)) {
			let value: number = 0;
			switch (mapping.fieldKey) {
				case 'lat':
					value = field.value.latitude;
					break;
				case 'lng':
					value = field.value.longitude;
					break;
				default:
					throw new Error('Invalid mapping key');
			}

			return { value: String(value), isValid: value !== 0, raw: rawValue };
		}

		const hasRawValue = rawValue != null && rawValue !== '';

		if (hasRawValue) {
			return { value: field.displayValue, isValid: field.hasValue(), raw: rawValue };
		} else {
			return { value: field.displayValue, isValid: true, raw: rawValue };
		}
	}

	reset() {
		for (const mapping of this.mappings) {
			mapping.csvIndex = -1;
			mapping.isValid = true;
		}
	}

	/**
	 * Attempt to guess some default mappings based off the headers in the CSV and the fields in the form
	 */
	setDefaultMappings() {
		const headerSearch: { [key: string]: number } = {};

		for (let i = 0; i < this.csv.headers.length; i++) {
			const header = this.csv.headers[i];
			headerSearch[header.toLowerCase()] = i;
		}
		let hasMapped = false;
		const applyMapping = (key: string, mapping: IkeCSVMapping): boolean => {
			if (mapping.csvIndex > -1) {
				return true;
			}

			const index = headerSearch[key];
			if (index != null) {
				delete headerSearch[key];
				this.setMapping(mapping, index, false);
				hasMapped = true;
				return true;
			}
			return false;
		};

		for (const mapping of this.mappings) {
			const { field, fieldKey } = mapping;

			// Is there a direct match between header name and field name
			if (applyMapping(field.displayName.toLowerCase(), mapping)) {
				continue;
			}

			// See if any other keys will map
			const searchKeys = DefaultKeyMappings[fieldKey];
			if (searchKeys == null) {
				continue;
			}
			for (const search of searchKeys) {
				if (applyMapping(search, mapping)) {
					break;
				}
			}
		}

		if (hasMapped) {
			this.processRows();
		}
	}

	get isValid(): boolean {
		if (this.csv.rows.length === 0) {
			return false;
		}
		let hasMappings = false;
		for (const field of this.mappings) {
			if (field.csvIndex > -1) {
				hasMappings = true;
			}
			if (field.isValid === false) {
				return false;
			}
		}
		return hasMappings;
	}

	private processRows() {
		this.processed = [];

		for (const field of this.mappings) {
			field.isValid = true;
		}

		for (const row of this.csv.rows) {
			this.processed.push(this.processRow(row));
		}
	}

	private processRow(row: string[]): CollectionModel {
		const collection = IkeModel.Collection.create(this.form, null); // TODO do we always need a loader?
		for (const mapping of this.mappings) {
			if (mapping.csvIndex === -1) {
				continue;
			}

			const field = collection.getField(mapping.field.id);
			const Mapper = MappingFunctions[mapping.field.type];
			const isValid = Mapper(field, row[mapping.csvIndex], mapping.fieldKey);
			if (isValid === false) {
				mapping.isValid = false;
			}
		}

		return collection;
	}
}
